<script lang="js" setup>
import { ref, onMounted, computed } from "vue";
import * as XLSX from 'xlsx';
import Swal from 'sweetalert2';
import { useToast } from 'vue-toast-notification';
import 'vue-toast-notification/dist/theme-default.css';
import { defineAsyncComponent } from 'vue';

const API_BASE_URL = process.env.VUE_APP_BASE_URL;
const allData = ref([]);
const elementsPerPage = ref(25);
const currentPage = ref(1);
const filterQuery = ref("");
const fromDate = ref("");
const toDate = ref("");
const toast = useToast();

// Function to format date to dd-mm-yyyy for display
const formatDate = (dateString) => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
};

// Function to format date to YYYY-MM-DD for input value
const formatDateForInput = (dateString) => {
    const date = new Date(dateString);
    return date.toISOString().split('T')[0];
};

// Function to convert date string to Date object with time set to start/end of day
const parseDate = (dateString, isEndDate = false) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    if (isEndDate) {
        date.setHours(23, 59, 59, 999);
    } else {
        date.setHours(0, 0, 0, 0);
    }
    return date;
};

onMounted(async () => {
    try {
        const response = await fetch(`${API_BASE_URL}api/operation/get/kyp/list`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();

        if (result && result.success && result.data && result.data.length > 0) {
            allData.value = result.data;
            console.log("allData", allData.value);
        } else {
            toast.error('No data available');
        }
    } catch (error) {
        console.error('Error fetching data:', error);
        toast.error('Failed to fetch data: ' + error.message);
    }
});

const filteredData = computed(() => {
    const query = filterQuery.value.toLowerCase();
    const from = fromDate.value ? parseDate(fromDate.value) : null;
    const to = toDate.value ? parseDate(toDate.value, true) : null;

    return allData.value.filter(site => {
        const matchesSearch = site.siteName.toLowerCase().includes(query) ||
            (site.employeeName && site.employeeName.toLowerCase().includes(query)) ||
            (site.employeeCode && site.employeeCode.toLowerCase().includes(query));

        const siteDate = new Date(site.kypDate);
        const matchesDateRange = (!from || siteDate >= from) && (!to || siteDate <= to);

        return matchesSearch && matchesDateRange;
    });
});

const paginatedData = computed(() => {
    const start = (currentPage.value - 1) * elementsPerPage.value;
    const end = start + elementsPerPage.value;
    return filteredData.value.slice(start, end);
});

function num_pages() {
    return Math.ceil(filteredData.value.length / elementsPerPage.value);
}

function change_page(page) {
    currentPage.value = page;
}

function previousPage() {
    if (currentPage.value > 1) {
        currentPage.value--;
    }
}

function nextPage() {
    if (currentPage.value < num_pages()) {
        currentPage.value++;
    }
}

function downloadExcel() {
    // Format dates and employee data before downloading
    const formattedData = allData.value.map(item => ({
        ...item,
        kypDate: formatDate(item.kypDate),
        absentEmployees: item.absentEmployees ? item.absentEmployees.map(emp => `${emp.employeeName} (${emp.employeeCode})`).join(', ') : '',
        weeklyOffEmployees: item.weeklyOffEmployees ? item.weeklyOffEmployees.map(emp => `${emp.employeeName} (${emp.employeeCode})`).join(', ') : ''
    }));
    const worksheet = XLSX.utils.json_to_sheet(formattedData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "KYP Data");
    XLSX.writeFile(workbook, 'kyp.xlsx');
}
</script>

<template>
    <Breadcrumbs main="Apps" title="Kyp" />
    <div class="container-fluid">
        <div class="email-wrap bookmark-wrap">
            <div class="row card card-body">
                <div class="table-responsive product-table custom-scrollbar">
                    <form class="no-footer">
                        <div class="mb-3 row justify-content-between align-items-center">
                            <div class="col-12 col-md-4 mb-2 mb-md-0">
                                <label for="table-complete-search" class="form-label">Search:</label>
                                <input id="table-complete-search" type="text" class="form-control" name="searchTerm"
                                    v-model="filterQuery" placeholder="Search by site, employee name or code...">
                            </div>
                            <div class="col-12 col-md-6 mb-2 mb-md-0">
                                <div class="row align-items-center">
                                    <div class="col">
                                        <label for="from-date" class="form-label">From Date:</label>
                                        <input id="from-date" type="date" class="form-control" v-model="fromDate">
                                    </div>
                                    <div class="col">
                                        <label for="to-date" class="form-label">To Date:</label>
                                        <input id="to-date" type="date" class="form-control" v-model="toDate"
                                            :min="fromDate">
                                    </div>
                                </div>
                            </div>
                            <div class="col-12 col-md-2 text-end">
                                <button type="button" class="btn btn-primary" @click="downloadExcel">Download
                                    Excel</button>
                            </div>
                        </div>
                        <table class="display table-striped table-hover table-bordered table" id="basic-1">
                            <thead>
                                <tr>
                                    <th class="sorting_asc" scope="col" style="width: 50px;">S.No.</th>
                                    <th class="sorting_asc text-nowrap" scope="col">KYP Date</th>
                                    <th class="sorting_asc text-nowrap" scope="col">Site Name</th>
                                    <th class="sorting_asc text-nowrap" scope="col">Employee Name</th>
                                    <!-- <th class="sorting_asc text-nowrap" scope="col">Employee Code</th> -->
                                    <th class="sorting_asc text-nowrap" scope="col">Is KYP Given</th>
                                    <th class="sorting_asc text-nowrap" scope="col">Total no. of Manpower</th>
                                    <th class="sorting_asc text-nowrap" scope="col">Full</th>
                                    <th class="sorting_asc text-nowrap" scope="col">Vacant(if any)</th>
                                    <th class="sorting_asc text-nowrap" scope="col">Vacant Reliever</th>
                                    <th class="sorting_asc text-nowrap" scope="col">Change(if any)</th>
                                    <th class="sorting_asc text-nowrap" scope="col">Absent(if any)</th>
                                    <th class="sorting_asc text-nowrap" scope="col">Weekly Off</th>
                                    <th class="sorting_asc text-nowrap" scope="col">Managed With Reliever</th>
                                    <th class="sorting_asc text-nowrap" scope="col">Managed With OT</th>
                                    <th class="sorting_asc text-nowrap" scope="col">Not managed (NM)</th>
                                    <th class="sorting_asc text-nowrap" scope="col">Critical/Safe</th>
                                    <th class="sorting_asc text-nowrap" scope="col">Absent Employees</th>
                                    <th class="sorting_asc text-nowrap" scope="col">Weekly Off Employees</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(site, index) in paginatedData" :key="index">
                                    <td>{{ index + 1 + (currentPage - 1) * elementsPerPage }}</td>
                                    <td>{{ formatDate(site.kypDate) }}</td>
                                    <td>{{ site.siteName }}</td>
                                    <td>{{ site.employeeName }} - {{ site.employeeCode }}</td>
                                    <td>{{ site.isKYPGiven ? 'Yes' : 'No' }}</td>
                                    <td>{{ site.totalManpower }}</td>
                                    <td>{{ site.isFull ? 'Yes' : 'No' }}</td>
                                    <td>{{ site.vacantPost }}</td>
                                    <td>{{ site.vacantReliever }}</td>
                                    <td>{{ site.changeStaff }}</td>
                                    <td>{{ site.absent }}</td>
                                    <td>{{ site.weeklyOff }}</td>
                                    <td>{{ site.managedWithReliever }}</td>
                                    <td>{{ site.managedWithOT }}</td>
                                    <td>{{ site.notManaged }}</td>
                                    <td>{{ site.siteCondition }}</td>
                                    <td>
                                        <div v-if="site.absentEmployees && site.absentEmployees.length > 0">
                                            <div v-for="(emp, empIndex) in site.absentEmployees" :key="empIndex"
                                                class="mb-1">
                                                <small class="text-muted">{{ emp.employeeName }} ({{ emp.employeeCode
                                                    }})</small>
                                            </div>
                                        </div>
                                        <span v-else class="text-muted">-</span>
                                    </td>
                                    <td>
                                        <div v-if="site.weeklyOffEmployees && site.weeklyOffEmployees.length > 0">
                                            <div v-for="(emp, empIndex) in site.weeklyOffEmployees" :key="empIndex"
                                                class="mb-1">
                                                <small class="text-muted">{{ emp.employeeName }} ({{ emp.employeeCode
                                                    }})</small>
                                            </div>
                                        </div>
                                        <span v-else class="text-muted">-</span>
                                    </td>
                                </tr>
                            </tbody>
                        </table>

                        <ul class="pagination py-2 justify-content-end pagination-primary">
                            <li class="page-item" @click="previousPage">
                                <a class="page-link">Previous</a>
                            </li>
                            <li class="page-item" v-for="i in num_pages()" :key="i"
                                :class="[i == currentPage ? 'active' : '']" @click="change_page(i)">
                                <a class="page-link">{{ i }}</a>
                            </li>
                            <li class="page-item" @click="nextPage">
                                <a class="page-link">Next</a>
                            </li>
                        </ul>
                    </form>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
.form-label {
    margin-bottom: 0.5rem;
}

@media (max-width: 768px) {
    .row.justify-content-between {
        margin-bottom: 1rem;
    }

    .btn {
        margin-bottom: 0.5rem;
        width: 100%;
    }
}

/* Style the date inputs */
input[type="date"] {
    padding: 0.375rem 0.75rem;
    cursor: pointer;
}

input[type="date"]::-webkit-calendar-picker-indicator {
    cursor: pointer;
}

/* Style for employee lists in table cells */
.table td {
    vertical-align: top;
}

.table td .mb-1 {
    margin-bottom: 0.25rem !important;
}

.table td small {
    display: block;
    line-height: 1.2;
}
</style>